import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/utils';
import { fetchInvoiceDetail } from '../api';

interface InvoiceDetailDialogProps {
  invoiceId: string | null;
  onClose: () => void;
}

export default function InvoiceDetailDialog({ invoiceId, onClose }: InvoiceDetailDialogProps) {
  const open = invoiceId !== null;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ar-invoice-detail', invoiceId],
    queryFn: () => fetchInvoiceDetail(invoiceId as string),
    enabled: open,
    staleTime: 1000 * 60 * 5,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>
            {data ? `Invoice ${data.docNumber}` : 'Invoice'}
          </DialogTitle>
          <DialogDescription>
            {data ? `${data.customerName} · ${formatDate(data.invoiceDate)} → due ${formatDate(data.dueDate)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className='text-sm text-muted-foreground'>Loading from QuickBooks…</p>}

        {error && (
          <div className='text-sm text-red-600'>
            {(error as Error).message}{' '}
            <button type='button' className='underline' onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        {data && (
          <div className='space-y-4'>
            <div className='grid grid-cols-4 gap-3 text-xs'>
              <Field label='PO #' value={data.poNumber || '—'} />
              <Field label='Terms' value={data.paymentTerms || '—'} />
              <Field label='Project' value={data.projectName || '—'} />
              <Field label='Balance' value={formatCurrency(data.balance)} />
            </div>

            <div className='border rounded-md overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className='text-right'>Qty</TableHead>
                    <TableHead className='text-right'>Unit</TableHead>
                    <TableHead className='text-right'>Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.lines.map((l) => (
                    <TableRow key={l.lineNum}>
                      <TableCell className='max-w-sm truncate'>{l.description}</TableCell>
                      <TableCell>{l.itemName || '—'}</TableCell>
                      <TableCell className='text-right tabular-nums'>{l.quantity ?? '—'}</TableCell>
                      <TableCell className='text-right tabular-nums'>
                        {l.unitPrice !== null ? formatCurrency(l.unitPrice) : '—'}
                      </TableCell>
                      <TableCell className='text-right tabular-nums'>{formatCurrency(l.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className='flex justify-end gap-6 text-sm'>
              <Totals label='Subtotal' value={data.subtotal} />
              <Totals label='Tax' value={data.taxTotal} />
              <Totals label='Total' value={data.total} bold />
              <Totals label='Balance' value={data.balance} bold />
            </div>
          </div>
        )}

        <DialogFooter>
          {data?.quickbooksLink && (
            <Button asChild variant='outline'>
              <a href={data.quickbooksLink} target='_blank' rel='noreferrer'>
                <ExternalLink className='mr-2 h-4 w-4' />
                Open in QuickBooks
              </a>
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className='uppercase tracking-wide text-muted-foreground'>{label}</div>
      <div className='font-medium'>{value}</div>
    </div>
  );
}

function Totals({ label, value, bold = false }: { label: string; value: number; bold?: boolean }) {
  return (
    <div className='text-right'>
      <div className='text-[11px] uppercase tracking-wide text-muted-foreground'>{label}</div>
      <div className={bold ? 'font-bold tabular-nums' : 'tabular-nums'}>{formatCurrency(value)}</div>
    </div>
  );
}
